import React, { useState } from "react";
import styled from "styled-components";
import { Formik, Form, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import EmployerUser from "../../apiResources/EmployerUser";
import Employer from "../../apiResources/Employer";
import { FileUploadField } from "../library/Form";
import TextLink from "../library/TextLink";
import SettingLabel from "./SettingLabel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

type FormValues = {
  employerCustomTerms?: string;
};

const label = "Full terms file upload";

const TermsFileSettingsOption = (props: Props) => {
  const { user, company, isEditing, disabled, onEditStart, onEditComplete } =
    props;

  const [linkUrl, setLinkUrl] = useState(company.customTermsUrl);

  const submit = async (values, helpers: FormikHelpers<FormValues>) => {
    try {
      company.assignAttributes(values);
      const success = await company.save();

      if (success) {
        setLinkUrl(company.customTermsUrl);
        onEditComplete();
      } else {
        helpers.setErrors({
          employerCustomTerms: company.errors.employerCustomTerms.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      helpers.setErrors({
        employerCustomTerms: "Something went wrong uploading the pdf",
      });
    } finally {
      company.rollback();
    }
  };

  if (linkUrl && !isEditing) {
    return (
      <Wrapper>
        <Header>
          <SettingLabel>{label}</SettingLabel>
          {user.isAdmin && (
            <TextLink disabled={disabled} onClick={onEditStart}>
              Edit
            </TextLink>
          )}
        </Header>
        <a href={linkUrl} target="_blank" rel="noreferrer">
          View pdf <FontAwesomeIcon icon={faExternalLinkAlt} />
        </a>
      </Wrapper>
    );
  }

  return (
    <div>
      <Header>
        <SettingLabel>{label}</SettingLabel>
        {isEditing && <TextLink onClick={onEditComplete}>Cancel</TextLink>}
      </Header>

      <Formik initialValues={{ employerCustomTerms: "" }} onSubmit={submit}>
        {({ submitForm, isSubmitting }) => (
          <Form>
            <FileUploadField
              accept=".pdf"
              name="employerCustomTerms"
              label={label}
              hiddenLabel
              onChange={() => submitForm()}
            >
              {isSubmitting && <UploadStatus>Uploading pdf...</UploadStatus>}
            </FileUploadField>
          </Form>
        )}
      </Formik>
    </div>
  );
};

type Props = {
  user: EmployerUser;
  company: Employer;
  isEditing?: boolean;
  disabled?: boolean;
  onEditStart: () => void;
  onEditComplete: () => void;
};

const Wrapper = styled.div`
  margin-bottom: 1rem;
`;

const UploadStatus = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default TermsFileSettingsOption;
